import axios from 'axios'
import { P } from 'components'
import ButtonIcon from 'components/Buttons/ButtonIcon'
import LoadingAnimation from 'components/LoadingAnimation'
import { passwordRequirements } from 'constants'
import {
  logout,
  setAlertMsg,
  toggleAlert,
} from 'features/public-views/login/loginSlice'
import { useEffect, useState } from 'react'
import { AlertCircle, ArrowLeft, Circle, Eye, EyeOff } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  CardBody,
  CardText,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  ListGroup,
  ListGroupItem,
} from 'reactstrap'
import { apiEndpoint } from 'utility/Utils'
import IcCheckCircle from 'assets/icons/ic_check_circle.svg'
import { useSnackbar } from 'notistack'

const PasswordSetting = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { token } = useSelector((s) => s.auth)
  const [showPassword, setShowPassword] = useState(false)
  const [showOldPassword, setShowOldPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [password, setPassword] = useState('')
  const [oldPassword, setOldPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isMatch, setIsMatch] = useState(false)
  const [hasType, setHasType] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errMsg, setErrMsg] = useState('')
  const [passwordRequirementsData, setPasswordRequirementsData] = useState(
    passwordRequirements.map((item) => ({ ...item, checked: false })),
  )

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  const handleChangeOldPass = (e) => {
    const value = e.target.value
    setOldPassword(value)
    setErrMsg('')
  }

  const handleChangeNewPass = (e) => {
    const value = e.target.value
    setPassword(value)
    const updatedRequirements = passwordRequirements.map((req) => {
      let isChecked = false
      switch (req.id) {
        case 1:
          isChecked = /[a-z]/.test(value)
          break
        case 2:
          isChecked = /[A-Z]/.test(value)
          break
        case 3:
          isChecked = value.length >= 8
          break
        case 4:
          isChecked = /[0-9]/.test(value)
          break
        case 5:
          isChecked = /[^a-zA-Z0-9]/.test(value)
          break
        case 6:
          isChecked = value === confirmPassword
          break
        default:
          break
      }
      return { ...req, checked: isChecked }
    })
    setPasswordRequirementsData(updatedRequirements)
    setErrMsg('')
  }

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value
    setConfirmPassword(value)
    setErrMsg('')
    setPasswordRequirementsData((prev) =>
      prev.map((req) =>
        req.id === 6 ? { ...req, checked: password === value } : req,
      ),
    )
  }

  const handleChangePass = async () => {
    try {
      if (isMatch) {
        setLoading(true)
        const res = await axios.post(
          `${apiEndpoint}/api/v1/bussines/auth/changepassword`,
          {
            newpassword: password,
            oldpassword: oldPassword,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        )
        if (res.status === 200) {
          setPassword('')
          setOldPassword('')
          setConfirmPassword('')
          dispatch(logout())
          dispatch(toggleAlert(true))
          dispatch(
            setAlertMsg(
              'Anda telah mengganti password. Silakan login ulang untuk melanjutkan.',
            ),
          )
          navigate('/login')
        } else {
          throw res
        }
      }
    } catch (error) {
      enqueueSnackbar(error.response.data.message || 'Terjadi kesalahan', {
        variant: 'error',
      })
      setErrMsg(error.response.data.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (
      oldPassword &&
      password === confirmPassword &&
      passwordRequirementsData.every((item) => item.checked)
    ) {
      setIsMatch(true)
    } else {
      setIsMatch(false)
    }

    if (oldPassword !== '' && password !== '' && confirmPassword !== '') {
      setHasType(true)
    } else {
      setHasType(false)
    }
  }, [oldPassword, password, confirmPassword, passwordRequirementsData])

  return (
    <CardBody>
      <ListGroup flush>
        <ListGroupItem className="py-4">
          <div className="d-flex justify-content-between align-items-center">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ArrowLeft
                size={20}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(-1)}
              />
              <CardText tag="h4">Ganti password</CardText>
            </div>
          </div>
        </ListGroupItem>
        <div className="pt-4 pl-4">
          <FormGroup style={{ width: '90%' }}>
            <P>Password lama</P>
            <InputGroup className="mt-1">
              <Input
                style={{ maxWidth: '512px' }}
                type={showOldPassword ? 'text' : 'password'}
                onChange={(e) => {
                  handleChangeOldPass(e)
                }}
                value={oldPassword}
                placeholder="Masukkan password lama"
              />
              <InputGroupText
                style={{ borderLeft: 'none' }}
                className="bg-transparent"
              >
                <div style={{ cursor: 'pointer' }}>
                  {showOldPassword ? (
                    <EyeOff
                      style={{ color: 'var(--grey-500)' }}
                      onClick={toggleOldPasswordVisibility}
                    />
                  ) : (
                    <Eye
                      style={{ color: 'var(--grey-500)' }}
                      onClick={toggleOldPasswordVisibility}
                    />
                  )}
                </div>
              </InputGroupText>
            </InputGroup>
          </FormGroup>
        </div>
        <div className="pl-4">
          <FormGroup style={{ width: '90%' }}>
            <P>Password baru</P>
            <InputGroup className="mt-1">
              <Input
                style={{ maxWidth: '512px' }}
                type={showPassword ? 'text' : 'password'}
                onChange={(e) => {
                  handleChangeNewPass(e)
                }}
                value={password}
                placeholder="Masukkan password baru"
              />
              <InputGroupText
                style={{ borderLeft: 'none' }}
                className="bg-transparent"
              >
                <div style={{ cursor: 'pointer' }}>
                  {showPassword ? (
                    <EyeOff
                      style={{ color: 'var(--grey-500)' }}
                      onClick={togglePasswordVisibility}
                    />
                  ) : (
                    <Eye
                      style={{ color: 'var(--grey-500)' }}
                      onClick={togglePasswordVisibility}
                    />
                  )}
                </div>
              </InputGroupText>
            </InputGroup>
          </FormGroup>
        </div>
        <div className="pl-4">
          <FormGroup style={{ width: '90%' }}>
            <P>Konfirmasi Password baru</P>
            <InputGroup className="mt-1">
              <Input
                style={{ maxWidth: '512px' }}
                type={showConfirmPassword ? 'text' : 'password'}
                onChange={(e) => {
                  handleConfirmPasswordChange(e)
                }}
                value={confirmPassword}
                valid={isMatch && hasType}
                invalid={!isMatch && hasType}
                placeholder="Masukkan ulang password baru"
              />
              <InputGroupText
                style={{ borderLeft: 'none' }}
                className="bg-transparent"
              >
                <div style={{ cursor: 'pointer' }}>
                  {showConfirmPassword ? (
                    <EyeOff
                      style={{ color: 'var(--grey-500)' }}
                      onClick={toggleConfirmPasswordVisibility}
                    />
                  ) : (
                    <Eye
                      style={{ color: 'var(--grey-500)' }}
                      onClick={toggleConfirmPasswordVisibility}
                    />
                  )}
                </div>
              </InputGroupText>
            </InputGroup>
          </FormGroup>
          {errMsg && (
            <FormGroup>
              <small className="text-danger">
                <AlertCircle size={15} /> {errMsg}
              </small>
            </FormGroup>
          )}
        </div>
      </ListGroup>
      <div className="pl-4 pt-3">
        {passwordRequirementsData.map((el) => {
          return (
            <div className="d-flex align-items-center gap-2 mb-2" key={el.id}>
              {el.checked ? (
                <img
                  src={IcCheckCircle}
                  width={20}
                  height={20}
                  alt={`checked-${el.label}`}
                />
              ) : (
                <Circle size={20} />
              )}
              <P size="regular" color="#344054">
                {el.label}
              </P>
            </div>
          )
        })}
      </div>
      <div className="d-flex align-items-center gap-2 pl-4 mt-4 mb-3">
        <ButtonIcon
          btnTitle="Simpan"
          btnProps={{
            className: 'w-auto',
            outline: false,
            size: 'sm',
            disabled: loading || !hasType || !oldPassword || !isMatch,
            onClick: handleChangePass,
          }}
        />
      </div>
    </CardBody>
  )
}

export default PasswordSetting
